import {
  ConstructionConditionOption,
  EfficiencyClassOption,
  EnergySourceOption,
  HeatingTypeOption,
  ParkingSpaceTypeOption,
  BuildingTypeOption,
} from '../../../../@types/dropdown-types'
import { EnergyCertificateAvailability } from '../../../../@types/radio-types'
import { ContentStateInterface, InteriorQuality } from '../../../../@types/types'
import { AiExtractionResponse, EstateFeaturesPrefill } from '../../../../api/aiDataExtraction/types'
import { DE, formatDecimal } from '../../../../i18next/decimal'

type ExtractionEnumFieldKey =
  | 'constructionCondition'
  | 'constructionHeatingType'
  | 'efficiencyClass'
  | 'interiorQuality'
  | 'parkingSpaceType'

const CONSTRUCTION_CONDITION_VALUES = new Set(Object.values(ConstructionConditionOption))
const HEATING_TYPE_VALUES = new Set(Object.values(HeatingTypeOption))
const ENERGY_SOURCE_VALUES = new Set(Object.values(EnergySourceOption))
const EFFICIENCY_CLASS_VALUES = new Set(Object.values(EfficiencyClassOption))
const INTERIOR_QUALITY_VALUES = new Set(Object.values(InteriorQuality))
const PARKING_SPACE_TYPE_VALUES = new Set(Object.values(ParkingSpaceTypeOption))
const BUILDING_TYPE_VALUES = new Set(Object.values(BuildingTypeOption))

export const mergeAiExtractionResponseIntoInsertionData = (
  insertionData: ContentStateInterface,
  resp: AiExtractionResponse
): ContentStateInterface => {
  const prefilledData = { ...insertionData }

  setIfEmpty(prefilledData, 'livingSpace', toOptionalGermanDecimalString(resp.livingSpace, 2))
  setIfEmpty(prefilledData, 'room', toOptionalGermanDecimalString(resp.room, 1))
  setIfEmpty(prefilledData, 'noBathrooms', toOptionalIntegerString(resp.noBathrooms))
  setIfEmpty(prefilledData, 'noBedrooms', toOptionalIntegerString(resp.noBedrooms))
  setIfEmpty(prefilledData, 'usableArea', toOptionalGermanDecimalString(resp.usableArea, 2))
  setIfEmpty(prefilledData, 'plotArea', toOptionalGermanDecimalString(resp.plotArea, 2))
  const constructionYearFromCertificate = toOptionalIntegerString(resp.constructionYear)
  if (constructionYearFromCertificate !== undefined) {
    setIfEmpty(prefilledData, 'legalConstructionYear', constructionYearFromCertificate)
    setIfEmpty(prefilledData, 'energyCertificateAvailability', EnergyCertificateAvailability.AVAILABLE)
  }
  setIfEmpty(prefilledData, 'constructionLastModernization', resp.constructionLastModernization)
  setIfEmpty(prefilledData, 'street', toOptionalTrimmedString(resp.street))
  setIfEmpty(prefilledData, 'houseNumber', toOptionalTrimmedString(resp.houseNumber))
  setIfEmpty(prefilledData, 'city', toOptionalTrimmedString(resp.city))
  setIfEmpty(prefilledData, 'postcode', toOptionalTrimmedString(resp.postcode))
  setIfEmpty(prefilledData, 'thermalCharacteristic', toOptionalGermanDecimalString(resp.thermalCharacteristic, 2))
  setIfEmpty(prefilledData, 'noParkingSpaces', resp.noParkingSpaces)

  if (isBuildingTypeValue(resp.buildingType) && !hasDropdownValue(prefilledData.buildingType)) {
    prefilledData.buildingType = {
      value: resp.buildingType,
      label: resp.buildingType,
    }
  }

  setIfExtractedEnumIsValid(
    prefilledData,
    'constructionCondition',
    resp.constructionCondition,
    isConstructionConditionValue
  )
  setIfExtractedEnumIsValid(prefilledData, 'constructionHeatingType', resp.constructionHeatingType, isHeatingTypeValue)
  setIfExtractedEnumIsValid(prefilledData, 'efficiencyClass', resp.efficiencyClass, isEfficiencyClassValue)
  setIfExtractedEnumIsValid(prefilledData, 'interiorQuality', resp.interiorQuality, isInteriorQualityValue)
  setIfExtractedEnumIsValid(prefilledData, 'parkingSpaceType', resp.parkingSpaceType, isParkingSpaceTypeValue)

  if (isValueMissing(prefilledData.constructionEnergySource)) {
    const energySources = (resp.constructionEnergySource ?? []).filter(isEnergySourceValue)
    if (energySources.length > 0) {
      prefilledData.constructionEnergySource = energySources
    }
  }

  prefilledData.estateFeatures = mergeEstateFeatures(prefilledData.estateFeatures, resp.estateFeatures)

  return prefilledData
}

const mergeEstateFeatures = (
  currentEstateFeatures: ContentStateInterface['estateFeatures'],
  extractedEstateFeatures: EstateFeaturesPrefill | undefined
) => {
  if (!extractedEstateFeatures) {
    return currentEstateFeatures
  }

  const nextEstateFeatures = { ...(currentEstateFeatures ?? {}) }
  let hasChanges = false

  Object.entries(extractedEstateFeatures).forEach(([featureName, featureValue]) => {
    if (featureValue === true && nextEstateFeatures[featureName as keyof typeof nextEstateFeatures] !== true) {
      nextEstateFeatures[featureName as keyof typeof nextEstateFeatures] = true
      hasChanges = true
    }
  })

  return hasChanges ? nextEstateFeatures : currentEstateFeatures
}

const setIfEmpty = <Key extends keyof ContentStateInterface>(
  insertionData: ContentStateInterface,
  key: Key,
  nextValue: ContentStateInterface[Key] | undefined
) => {
  if (nextValue === undefined || !isValueMissing(insertionData[key])) {
    return
  }

  insertionData[key] = nextValue
}

const isNoInformationPlaceholder = (value: unknown): boolean => value === 'NO_INFORMATION'

const setIfExtractedEnumIsValid = <Key extends ExtractionEnumFieldKey>(
  insertionData: ContentStateInterface,
  key: Key,
  candidate: string | undefined,
  isValid: (value: string | undefined) => value is NonNullable<ContentStateInterface[Key]>
) => {
  const current = insertionData[key]
  if (isValid(candidate) && (isValueMissing(current) || isNoInformationPlaceholder(current))) {
    insertionData[key] = candidate
  }
}

const toOptionalTrimmedString = (value: string | undefined) => {
  if (!value) {
    return undefined
  }

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : undefined
}

const toFiniteNumber = (value: number | undefined | null): number | undefined => {
  if (value === undefined || value === null) {
    return undefined
  }

  return Number.isFinite(value) ? value : undefined
}

const toOptionalGermanDecimalString = (value: number | undefined, maxFractionDigits: 1 | 2): string | undefined => {
  const n = toFiniteNumber(value)
  if (n === undefined) {
    return undefined
  }

  const rounded = Number(n.toFixed(maxFractionDigits))
  return formatDecimal(String(rounded), DE, 0, maxFractionDigits) || undefined
}

const toOptionalIntegerString = (value: number | undefined): string | undefined => {
  const n = toFiniteNumber(value)
  if (n === undefined) {
    return undefined
  }

  return String(Math.round(n))
}

const hasDropdownValue = (value: ContentStateInterface['buildingType']) => !!value?.value

const isValueMissing = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true
  }

  if (typeof value === 'string') {
    return value.trim().length === 0
  }

  if (Array.isArray(value)) {
    return value.length === 0
  }

  return false
}

const isConstructionConditionValue = (value: string | undefined): value is ConstructionConditionOption =>
  !!value && CONSTRUCTION_CONDITION_VALUES.has(value as ConstructionConditionOption)

const isHeatingTypeValue = (value: string | undefined): value is HeatingTypeOption =>
  !!value && HEATING_TYPE_VALUES.has(value as HeatingTypeOption)

const isEnergySourceValue = (value: string): value is EnergySourceOption =>
  ENERGY_SOURCE_VALUES.has(value as EnergySourceOption)

const isEfficiencyClassValue = (value: string | undefined): value is EfficiencyClassOption =>
  !!value && EFFICIENCY_CLASS_VALUES.has(value as EfficiencyClassOption)

const isInteriorQualityValue = (value: string | undefined): value is InteriorQuality =>
  !!value && INTERIOR_QUALITY_VALUES.has(value as InteriorQuality)

const isParkingSpaceTypeValue = (value: string | undefined): value is ParkingSpaceTypeOption =>
  !!value && PARKING_SPACE_TYPE_VALUES.has(value as ParkingSpaceTypeOption)

const isBuildingTypeValue = (value: string | undefined): value is BuildingTypeOption =>
  !!value && BUILDING_TYPE_VALUES.has(value as BuildingTypeOption)
